/**
 * Light, typed wrapper around the new report-first API endpoints.
 *
 * Co-located here so the existing `api.ts` (already 1700+ lines) stays
 * untouched. All endpoints use credentials: 'include' so the session
 * cookie is automatically sent.
 */

import { API_BASE, UnauthorizedError } from '../api';

// ── Shared shapes (mirrors /portal/src/types.ts) ──────────────────────────

export type ReportVariant = 'free' | 'premium';
export type SubscriptionPlan = 'free' | 'pro' | 'scale';
export type SubscriptionStatus = 'active' | 'past_due' | 'canceled' | 'trialing';

export interface Entitlement {
  plan: SubscriptionPlan;
  status: SubscriptionStatus;
  premium_unlocked: boolean;
  free_used: number;
  free_lifetime_cap: number;
  free_remaining: number;
  can_generate_free: boolean;
  current_period_end: string | null;
}

export interface ReportListItem {
  id: number;
  variant: ReportVariant;
  plan_at_generation: SubscriptionPlan;
  platform: string | null;
  handle: string | null;
  profile_url: string | null;
  pdf_object_key: string | null;
  created_at: string;
}

export interface CurrentLegalVersions {
  terms_version: string;
  privacy_version: string;
  terms_title: string;
  privacy_title: string;
  terms_effective_at: string;
  privacy_effective_at: string;
}

// ── Report sections (mirrors server reportSections.ts) ───────────────────

import type {
  CreatorSnapshotSection,
  AudienceIntelligenceSection,
  CohortBenchmarkSection,
  MonetizationOpportunityOverview,
  ContentSignalsSection,
  ProductMatchCard,
  VendorMatchCard,
  BrandDealOpportunity,
  RecommendedAction,
} from '../types/reportSections';
export type {
  CreatorSnapshotSection,
  AudienceIntelligenceSection,
  CohortBenchmarkSection,
  MonetizationOpportunityOverview,
  ContentSignalsSection,
  ProductMatchCard,
  VendorMatchCard,
  BrandDealOpportunity,
  RecommendedAction,
};

export interface FreeReportSections {
  variant: 'free';
  snapshot: CreatorSnapshotSection;
  audience: AudienceIntelligenceSection;
  cohort: CohortBenchmarkSection;
  monetization_overview: MonetizationOpportunityOverview;
  content_signals: ContentSignalsSection;
  action_plan_basic: RecommendedAction[];
  premium_preview: { sections: string[]; bullets: string[] };
}

export interface PremiumReportSections extends Omit<FreeReportSections, 'variant'> {
  variant: 'premium';
  product_matches: ProductMatchCard[];
  vendor_matches: VendorMatchCard[];
  brand_deal_opportunities: BrandDealOpportunity[];
  monetization_full: {
    by_category_breakdown: Array<{
      category: string;
      monthly_low: number;
      monthly_expected: number;
      monthly_high: number;
      drivers: string[];
    }>;
  };
  action_plan_full: RecommendedAction[];
}

export type ReportSections = FreeReportSections | PremiumReportSections;

export interface ReportPayload {
  id: number;
  variant: ReportVariant;
  plan_at_generation: SubscriptionPlan;
  platform: string | null;
  handle: string | null;
  profile_url: string | null;
  created_at: string;
  pdf_object_key: string | null;
  sections: ReportSections;
}

// ── Fetch wrapper ────────────────────────────────────────────────────────

async function call<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const resp = await fetch(`${API_BASE}${endpoint}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
    ...options,
  });
  let body: unknown = null;
  try { body = await resp.json(); } catch { /* ignore */ }
  if (!resp.ok) {
    const msg = ((body as { error?: string })?.error) ?? `Request failed (${resp.status})`;
    if (resp.status === 401) throw new UnauthorizedError(msg);
    const err = new Error(msg) as Error & { status?: number; payload?: unknown };
    err.status = resp.status;
    err.payload = body;
    throw err;
  }
  return body as T;
}

// ── Reports API ──────────────────────────────────────────────────────────

export const reportsApi = {
  generate: (input: { profile_url: string; force_refresh?: boolean }) =>
    call<{ data: { report_id: number; variant: ReportVariant; sections: ReportSections; entitlement: Entitlement } }>(
      '/reports/generate',
      { method: 'POST', body: JSON.stringify(input) }
    ),

  list: (limit = 20) =>
    call<{ data: { reports: ReportListItem[]; entitlement: Entitlement } }>(
      `/reports?limit=${limit}`
    ),

  get: (id: number) =>
    call<{ data: { report: ReportPayload; entitlement: Entitlement } }>(
      `/reports/${id}`
    ),

  download: (id: number, variant: ReportVariant) =>
    call<{ data: { url: string; expires_in: number } }>(
      `/reports/${id}/download?variant=${variant}`
    ),

  uploadPdf: async (id: number, variant: ReportVariant, pdfBytes: ArrayBuffer) => {
    const resp = await fetch(`${API_BASE}/reports/${id}/render-pdf?variant=${variant}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/pdf' },
      body: pdfBytes,
    });
    if (!resp.ok) {
      let msg = `Upload failed (${resp.status})`;
      try { const j = await resp.json() as { error?: string }; if (j.error) msg = j.error; } catch { /* */ }
      throw new Error(msg);
    }
    return (await resp.json()) as { data: { object_key: string } };
  },

  entitlement: () =>
    call<{ data: Entitlement }>('/reports/me/entitlement'),
};

// ── Billing API ──────────────────────────────────────────────────────────

export const billingApi = {
  me: () =>
    call<{
      data: {
        subscription: { plan: SubscriptionPlan; status: SubscriptionStatus; current_period_end: string | null } | null;
        entitlement: Entitlement;
        pricing: Record<string, { monthly_usd: number; features: string[] }>;
      };
    }>('/billing/me'),

  checkout: (plan: 'pro' | 'scale', returnUrl?: string) =>
    call<{ data: { redirect_url: string; dev?: boolean; plan: 'pro' | 'scale' } }>(
      '/billing/checkout',
      { method: 'POST', body: JSON.stringify({ plan, return_url: returnUrl }) }
    ),

  cancel: () => call<{ data: unknown }>('/billing/cancel', { method: 'POST' }),
};

// ── Legal API ────────────────────────────────────────────────────────────

export const legalApi = {
  current: () => call<{ data: CurrentLegalVersions }>('/legal/current'),

  document: (kind: 'terms' | 'privacy', version: string) =>
    call<{
      data: { kind: string; version: string; title: string; body_markdown: string; effective_at: string };
    }>(`/legal/document/${kind}/${version}`),

  recordConsent: (input: {
    email: string;
    terms_version: string;
    privacy_version: string;
    marketing_optin?: boolean;
  }) =>
    call<{ data: { id: number; accepted_at: string } }>(
      '/legal/consent',
      { method: 'POST', body: JSON.stringify(input) }
    ),

  myConsent: () =>
    call<{
      data: {
        latest: { id: number; accepted_at: string; terms_version: string; privacy_version: string; marketing_optin: boolean } | null;
        current_versions: CurrentLegalVersions | null;
        is_current: boolean;
      };
    }>('/legal/me/consent'),
};
